import { useEffect, useState } from "react";
import { Settings2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "../ui/switch";
import api, { formatError } from "../../lib/api";
import { inputCls } from "../../lib/outreach";
import ReplyTemplatesEditor from "./ReplyTemplatesEditor";

const FIELDS = [
  ["booking_url", "Booking link (only link allowed besides unsubscribe)", "https://outlook.office.com/bookwithme/…"],
  ["unsubscribe_base_url", "Unsubscribe link domain", "https://crm.kingofit.ca"],
  ["company_name", "Company name", "King of IT"],
  ["company_address", "Mailing address", "123 Example St, Toronto, ON M5V 1A1"],
  ["company_phone", "Phone", "(416) 555-0100"],
];

export default function OutreachSettingsCard({ isAdmin, onChanged }) {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/outreach/settings").then(({ data }) => setS(data)).catch(() => {}); }, []);

  const save = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.put("/outreach/settings", patch);
      setS(data);
      toast.success("Outreach settings saved");
      onChanged?.();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!s) return null;
  const set = (k) => (e) => setS({ ...s, [k]: e.target.value });

  return (
    <div data-testid="outreach-settings-card" className="rounded-xl border border-slate-800 bg-[#131C31] p-5 mb-6">
      <h2 className="font-display font-bold text-base inline-flex items-center gap-2 mb-4"><Settings2 className="w-5 h-5 text-amber-400" /> Sending settings</h2>
      <div className="flex flex-wrap gap-6 mb-5">
        <label className="inline-flex items-center gap-3 text-sm">
          <Switch data-testid="auto-approve-switch" disabled={!isAdmin || busy} checked={s.auto_approve} onCheckedChange={(v) => save({ auto_approve: v })} />
          Auto-approve outreach drafts
        </label>
        <label className="inline-flex items-center gap-3 text-sm">
          <Switch data-testid="pause-all-switch" disabled={!isAdmin || busy} checked={s.paused} onCheckedChange={(v) => save({ paused: v })} />
          <span className={s.paused ? "text-red-300 font-semibold" : ""}>Pause all sending</span>
        </label>
      </div>
      {isAdmin && (
        <form onSubmit={(e) => { e.preventDefault(); save({ signature: s.signature, booking_url: s.booking_url, unsubscribe_base_url: s.unsubscribe_base_url, company_name: s.company_name, company_address: s.company_address, company_phone: s.company_phone, reply_templates: s.reply_templates, daily_first_email_limit: s.daily_first_email_limit }); }}>
          <label className="block text-xs font-medium text-slate-400 mb-1.5">Required signature (must appear in every email, incl. mailing address)</label>
          <p className="text-[11px] text-slate-500 mb-1.5">Every email body must also contain the placeholder <code className="text-amber-300">{"{{unsubscribe_link}}"}</code>. It's replaced with a unique link when the email is sent.</p>
          <textarea data-testid="signature-input" rows={4} className={`${inputCls} mb-3`} value={s.signature} onChange={set("signature")} placeholder={"Alex …\nKing of IT\n123 Example St, Toronto, ON\n(416) 555-0100"} />
          <div className="grid sm:grid-cols-2 gap-3 mb-3">
            {FIELDS.map(([k, label, ph]) => (
              <div key={k}>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">{label}</label>
                <input data-testid={`setting-${k}`} className={inputCls} value={s[k] || ""} onChange={set(k)} placeholder={ph} />
              </div>
            ))}
          </div>
          <div className="mb-3 max-w-xs">
            <label className="block text-xs font-medium text-slate-400 mb-1.5">Daily first-email limit (1–15)</label>
            <input data-testid="setting-daily-limit" type="number" min={1} max={15} className={inputCls} value={s.daily_first_email_limit ?? 5} onChange={(e) => setS({ ...s, daily_first_email_limit: Number(e.target.value) })} />
          </div>
          <p className="text-[11px] text-slate-500 mb-3">Unsubscribe links currently go to: <span className="text-slate-300">{s.effective_unsubscribe_base}/unsubscribe/…</span></p>
          <ReplyTemplatesEditor templates={s.reply_templates || []} onChange={(t) => setS({ ...s, reply_templates: t })} />
          <button data-testid="save-outreach-settings" disabled={busy} className="btn-primary rounded-lg px-4 py-2 text-sm inline-flex items-center gap-2">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save settings
          </button>
        </form>
      )}
    </div>
  );
}
